// src/components/KanbanBoard.js
import React, { useMemo } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import KanbanColumn from './KanbanColumn';

const KanbanBoard = ({ tickets, ticketGroups, isOverdueChecker, onDragEnd, smartLists, communities, onDeleteSmartList }) => {

    const { orderedColumns, groupedTickets } = useMemo(() => {
        const columns = [];
        const grouped = {};

        // --- 1. Process Smart Lists (auto-filtered, non-draggable columns) ---
        (smartLists || []).forEach(list => {
            columns.push({ _id: list.id, name: list.name, isSmartList: true });

            // Filter tickets based on the list's defined criteria
            grouped[list.id] = tickets.filter(ticket => {
                const f = list.filters;
                const communityMatch = f.communities.length === 0 || f.communities.includes(ticket.client);
                const statusMatch = f.statuses.length === 0 || f.statuses.includes(ticket.status);
                const priorityMatch = f.priorities.length === 0 || f.priorities.includes(ticket.priority);
                return communityMatch && statusMatch && priorityMatch;
            }).sort((a, b) => { // Sort smart lists by overdue status, then by newest
                const aIsOverdue = isOverdueChecker(a);
                const bIsOverdue = isOverdueChecker(b);
                if (aIsOverdue !== bIsOverdue) return aIsOverdue ? -1 : 1;
                return new Date(b.createdAt) - new Date(a.createdAt);
            });
        });
        
        // --- 2. Process regular, draggable ticket groups ---
        const unassignedGroup = { _id: 'unassigned', name: 'Unassigned', isSmartList: false };
        const sortedTicketGroups = [...(ticketGroups || [])].sort((a, b) => a.name.localeCompare(b.name));
        const draggableGroups = [unassignedGroup, ...sortedTicketGroups];

        draggableGroups.forEach(group => {
            columns.push(group);
            grouped[group._id] = tickets
                .filter(ticket => (ticket.ticketGroupId || 'unassigned') === group._id)
                .sort((a, b) => (a.position || 0) - (b.position || 0)); // Sort by manual position
        });
        
        const uniqueColumns = Array.from(new Map(columns.map(item => [item._id, item])).values());
        
        return { orderedColumns: uniqueColumns, groupedTickets: grouped };
    }, [tickets, ticketGroups, smartLists, isOverdueChecker, communities]);

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <div className="kanban-board-container">
                {orderedColumns.map(group => {
                    const ticketsForGroup = groupedTickets[group._id] || [];
                    return (
                        <KanbanColumn
                            key={group._id}
                            group={group}
                            tickets={ticketsForGroup}
                            isOverdueChecker={isOverdueChecker}
                            isDropDisabled={group.isSmartList}
                            isStaticList={group.isSmartList}
                            onDelete={group.isSmartList ? () => onDeleteSmartList(group._id) : null}
                        />
                    );
                })}
            </div>
        </DragDropContext>
    );
};

export default KanbanBoard;